import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Video, Users, CheckCircle2, Mic, PlayCircle, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEventDate, isPastEvent } from "@/lib/image";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const ytId = (u) => { const m = (u || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/); return m ? m[1] : ""; };
const vimeoId = (u) => { const m = (u || "").match(/vimeo\.com\/(?:video\/)?(\d+)/); return m ? m[1] : ""; };
const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
});

function RecordingPlayer({ ev }) {
  if (!ev) return null;
  const type = ev.recording_type || (ev.recording_url ? "youtube" : "");
  if (type === "file" && ev.recording_url) {
    const src = ev.recording_url.startsWith("http") ? ev.recording_url : `${process.env.REACT_APP_BACKEND_URL}${ev.recording_url}`;
    return <video controls autoPlay controlsList="nodownload" className="w-full h-full"><source src={src} /></video>;
  }
  const vm = vimeoId(ev.recording_url);
  if (type === "vimeo" || vm) { if (vm) return <iframe title={ev.title} src={`https://player.vimeo.com/video/${vm}?autoplay=1`} allow="autoplay; fullscreen" allowFullScreen className="w-full h-full" />; }
  const yt = ytId(ev.recording_url);
  if (yt) return <iframe title={ev.title} src={`https://www.youtube.com/embed/${yt}?autoplay=1`} allow="autoplay; encrypted-media; fullscreen" allowFullScreen className="w-full h-full" />;
  return <div className="w-full h-full flex items-center justify-center text-slate-400">Recording unavailable.</div>;
}

function EventCard({ ev, mine, onGet, onCancel, onWatch, past, busy }) {
  const recorded = ev.event_type === "recorded";
  const spotsLeft = ev.capacity ? Math.max(0, ev.capacity - (ev.registrations_count || 0)) : null;
  const full = !recorded && ev.capacity > 0 && spotsLeft === 0 && !mine;
  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden flex flex-col" data-testid={`event-card-${ev.id}`}>
      <div className="h-44 w-full bg-navy grain relative">
        {ev.cover_image ? <img src={ev.cover_image} alt={ev.title} className="h-full w-full object-cover" />
          : <div className="h-full w-full flex items-center justify-center">{recorded ? <PlayCircle className="h-10 w-10 text-gold/60" /> : <Calendar className="h-10 w-10 text-gold/60" />}</div>}
        <span className={`absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${recorded ? "bg-navy text-gold" : ev.mode === "online" ? "bg-emerald-500/90 text-white" : "bg-gold text-navy"}`}>
          {recorded ? "Recorded" : ev.mode === "online" ? "Live · Online" : "Live · In-person"}
        </span>
        {ev.price > 0
          ? <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/90 text-navy">₹{ev.price}</span>
          : <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/90 text-emerald-700">FREE</span>}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-lg font-semibold text-navy">{ev.title}</h3>
        {ev.speaker && <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Mic className="h-3.5 w-3.5 text-gold" /> {ev.speaker}</p>}
        {ev.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{ev.description}</p>}
        <div className="mt-3 space-y-1.5 text-sm text-slate-600">
          {!recorded && <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gold" /> {fmtEventDate(ev.starts_at)}</p>}
          {ev.duration && <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {ev.duration}</p>}
          {!recorded && ev.mode === "offline" && ev.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> {ev.location}</p>}
          {recorded && <p className="flex items-center gap-2"><Video className="h-4 w-4 text-gold" /> On-demand recording</p>}
          {!recorded && ev.capacity > 0 && !past && <p className="flex items-center gap-2"><Users className="h-4 w-4 text-gold" /> {spotsLeft} of {ev.capacity} spots left</p>}
        </div>
        <div className="mt-5 pt-4 border-t border-border">
          {recorded ? (
            mine ? (
              <button onClick={() => onWatch(ev)} data-testid={`watch-event-${ev.id}`} className="w-full h-10 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors inline-flex items-center justify-center gap-2"><PlayCircle className="h-4 w-4" /> Watch recording</button>
            ) : (
              <button onClick={() => onGet(ev)} disabled={busy} data-testid={`buy-event-${ev.id}`} className="w-full h-10 rounded-md bg-gold text-navy text-sm font-semibold hover:bg-gold-soft transition-colors disabled:opacity-60">
                {ev.price > 0 ? `Buy recording · ₹${ev.price}` : "Get free access"}
              </button>
            )
          ) : past ? (
            <span className="text-sm text-slate-400 font-medium">Event ended</span>
          ) : mine ? (
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Registered</span>
              {mine.join_link && <a href={mine.join_link} target="_blank" rel="noreferrer" data-testid={`join-event-${ev.id}`} className="inline-flex items-center gap-1 text-sm text-navy hover:text-gold font-medium">Join <ExternalLink className="h-3.5 w-3.5" /></a>}
              <button onClick={() => onCancel(ev)} data-testid={`cancel-event-${ev.id}`} className="text-sm text-slate-400 hover:text-red-600 transition-colors">Cancel</button>
            </div>
          ) : full ? (
            <span className="text-sm font-semibold text-red-500">Fully booked</span>
          ) : (
            <button onClick={() => onGet(ev)} disabled={busy} data-testid={`register-event-${ev.id}`} className="w-full h-10 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
              {ev.price > 0 ? `Buy ticket · ₹${ev.price}` : "Register · Free"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [mine, setMine] = useState([]); // full objects (join_link + recording_url)
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadMine = () => {
    if (!user) { setMine([]); return Promise.resolve(); }
    return api.get("/my/events").then((r) => setMine(r.data)).catch(() => {});
  };
  const load = () => api.get("/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { loadMine(); }, [user]);

  const mineFor = (id) => mine.find((e) => e.id === id);

  const onGet = async (ev) => {
    if (!user) { toast.message("Please log in to continue."); navigate("/login"); return; }
    if ((ev.price || 0) <= 0) {
      try { await api.post(`/events/${ev.id}/register`); toast.success(ev.event_type === "recorded" ? "Access granted!" : "You're registered!"); await load(); await loadMine(); }
      catch (e) { toast.error(apiErr(e)); }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${ev.id}/checkout/order`);
      const ok = await loadRazorpay();
      if (!ok) { setBusy(false); return toast.error("Could not load the payment window."); }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "KRV Prime", description: data.event_title,
        prefill: { name: data.user_name, email: data.user_email }, theme: { color: "#D4AF37" },
        handler: async (r) => {
          try { await api.post("/events/checkout/verify", { ...r, event_id: ev.id }); toast.success("Payment successful! Access granted."); await load(); await loadMine(); }
          catch { toast.error("Payment could not be verified. If money was deducted, contact us."); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      if (e?.response?.status === 503) { toast.info("Online payment is being set up. Please contact us to book."); navigate("/contact"); }
      else toast.error(apiErr(e));
      setBusy(false);
    }
  };

  const cancel = async (ev) => {
    try { await api.delete(`/events/${ev.id}/register`); toast.success("Registration cancelled"); await load(); await loadMine(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const watch = (ev) => { const m = mineFor(ev.id); setPlaying(m || ev); };

  const recorded = events.filter((e) => e.event_type === "recorded");
  const live = events.filter((e) => e.event_type !== "recorded");
  const upcoming = live.filter((e) => !isPastEvent(e.starts_at));
  const past = live.filter((e) => isPastEvent(e.starts_at));

  return (
    <div data-testid="events-page">
      <section className="bg-navy grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="eyebrow text-gold">Webinars & Events</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mt-3">Live Sessions & Recordings</h1>
          <p className="text-slate-300 mt-4 max-w-2xl">Join our live webinars and workshops, or purchase on-demand recordings of past sessions. Educational only — never investment advice.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">
        {loading ? <p className="text-slate-400">Loading events…</p>
          : events.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-border rounded-lg" data-testid="no-events">
              <Calendar className="h-10 w-10 text-slate-300 mx-auto" />
              <h3 className="font-display text-xl font-semibold text-navy mt-4">No events yet</h3>
              <p className="text-slate-500 mt-2">New webinars, workshops and recordings will appear here.</p>
            </div>
          ) : (
            <>
              <div>
                <h2 className="font-display text-2xl font-bold text-navy mb-6">Upcoming Live</h2>
                {upcoming.length === 0 ? <p className="text-slate-500">No upcoming live events right now.</p> : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {upcoming.map((ev) => <EventCard key={ev.id} ev={ev} mine={mineFor(ev.id)} onGet={onGet} onCancel={cancel} onWatch={watch} busy={busy} />)}
                  </div>
                )}
              </div>

              {recorded.length > 0 && (
                <div>
                  <h2 className="font-display text-2xl font-bold text-navy mb-6">On-Demand Recordings</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {recorded.map((ev) => <EventCard key={ev.id} ev={ev} mine={mineFor(ev.id)} onGet={onGet} onWatch={watch} busy={busy} />)}
                  </div>
                </div>
              )}

              {past.length > 0 && (
                <div>
                  <h2 className="font-display text-2xl font-bold text-navy mb-6">Past Live Events</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 opacity-80">
                    {past.map((ev) => <EventCard key={ev.id} ev={ev} mine={mineFor(ev.id)} onWatch={watch} past />)}
                  </div>
                </div>
              )}
            </>
          )}
      </div>

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="recording-modal">
          <DialogTitle className="sr-only">Event recording</DialogTitle>
          <div className="aspect-video w-full bg-black"><RecordingPlayer ev={playing} /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
